import { useRef, useEffect } from 'react';
import SectionLabel from '../components/SectionLabel';
import BookingCTA from '../components/BookingCTA';
import SEO from '../components/SEO';
import { ORGANIZATION_SCHEMA, LOCAL_BUSINESS_SCHEMA } from '../lib/schema';
import { useLang } from '../context/LanguageContext';

const MIKE_PHOTO = 'https://customer-assets-jt897jd0.emergentagent.net/job_shiny-clone/artifacts/tup20gtv_mike.png';

const Over = () => {
  const { c, ui } = useLang();
  const t = ui.over;
  const passionVideoRef = useRef(null);

  useEffect(() => {
    const vid = passionVideoRef.current;
    if (!vid) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          vid.play().catch(() => {});
        } else {
          vid.pause();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(vid);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-[#0a0a0b] text-white pt-32 pb-24" data-testid="over-page">
      <SEO
        path="/over"
        title="Over Shiny-Cleaning — het verhaal van Mike Bakker"
        description="Het persoonlijke verhaal achter Shiny-Cleaning in Deventer. Oprichter Mike Bakker over doorzetten na een ingrijpend ongeluk, zijn passie voor auto's en 12+ jaar ervaring in professionele autoverzorging."
        keywords="Shiny-Cleaning Deventer, Mike Bakker, auto detailing Deventer, professionele autoverzorging Deventer, over ons"
        jsonLd={[ORGANIZATION_SCHEMA, LOCAL_BUSINESS_SCHEMA]}
      />

      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">

        {/* PERSOONLIJKE VIDEO — bovenaan, verticale verhouding behouden */}
        <section className="mb-20 lg:mb-28 flex justify-center" data-testid="over-video">
          <div className="w-full max-w-[380px] rounded-2xl overflow-hidden border border-white/10 bg-black shadow-2xl">
            <video
              src="https://customer-assets-jt897jd0.emergentagent.net/job_shiny-clone/artifacts/856233378ac77921_70f6d868d71e4b4a91d22fcd743fb3aa.mov"
              controls
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="w-full h-auto block"
              data-testid="over-video-player"
            />
          </div>
        </section>

        {/* HERO — persoonlijke opening met foto van Mike */}
        <section className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center" data-testid="over-hero">
          <div>
            <SectionLabel>{t.label}</SectionLabel>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]" data-testid="over-h1">
              {t.h1}
            </h1>
            <p className="mt-7 text-white/70 text-lg leading-relaxed max-w-xl">{t.intro}</p>
            <div className="mt-9">
              <BookingCTA waMessage={t.teamWa} offerteTreatment={ui.over.label} />
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <img
              src={MIKE_PHOTO}
              alt="Mike Bakker, oprichter van Shiny-Cleaning in Deventer"
              className="w-full h-full object-cover aspect-[4/5]"
              data-testid="over-mike-photo"
            />
          </div>
        </section>

        {/* MIJN VERHAAL */}
        <section className="mt-28 lg:mt-36 max-w-3xl" data-testid="over-story">
          <SectionLabel>{t.storyLabel}</SectionLabel>
          <h2 className="mt-5 text-3xl md:text-4xl font-medium tracking-tight leading-[1.1]">{t.storyTitle}</h2>
          <div className="mt-8 space-y-6 border-l-2 border-[#2f6bff]/60 pl-6 md:pl-8">
            {t.story.map((p, i) => (
              <p key={i} className="text-white/75 text-lg leading-relaxed">{p}</p>
            ))}
          </div>
        </section>

        {/* VAN PASSIE NAAR SHINY-CLEANING — tekst + werkfoto */}
        <section className="mt-28 lg:mt-36 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-testid="over-passion">
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-black order-last lg:order-first">
            <video
              ref={passionVideoRef}
              src="https://customer-assets-jt897jd0.emergentagent.net/job_shiny-clone/artifacts/30fdbe5ba862f715_1775759408976.mov"
              controls
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full h-auto block"
              data-testid="over-passion-video"
            />
          </div>
          <div>
            <SectionLabel>{t.passionLabel}</SectionLabel>
            <h2 className="mt-5 text-3xl md:text-4xl font-medium tracking-tight leading-[1.1]">{t.passionTitle}</h2>
            <div className="mt-7 space-y-5">
              {t.passion.map((p, i) => (
                <p key={i} className="text-white/70 leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* DE PERSOON ACHTER HET BEDRIJF */}
        <section className="mt-28 lg:mt-36 max-w-3xl" data-testid="over-person">
          <SectionLabel>{t.personLabel}</SectionLabel>
          <h2 className="mt-5 text-3xl md:text-4xl font-medium tracking-tight leading-[1.1]">{t.personTitle}</h2>
          <div className="mt-7 space-y-5">
            {t.person.map((p, i) => (
              <p key={i} className="text-white/70 text-lg leading-relaxed">{p}</p>
            ))}
          </div>
        </section>

        {/* WAARDEN */}
        <section className="mt-28 lg:mt-36" data-testid="over-values">
          <SectionLabel>{t.valuesLabel}</SectionLabel>
          <h2 className="mt-5 text-3xl md:text-4xl font-medium tracking-tight leading-[1.1] max-w-3xl mb-12">{t.valuesTitle}</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {c.values.map(v => (
              <div key={v.number} className="p-8 rounded-xl border border-white/10 bg-white/[0.02]">
                <div className="text-[#2f6bff] text-sm tracking-[0.3em]">{v.number}</div>
                <h3 className="mt-4 text-2xl font-medium">{v.title}</h3>
                <p className="mt-3 text-white/60 text-sm">{v.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PIM EN HET TEAM */}
        <section className="mt-28 lg:mt-36" data-testid="over-team">
          <SectionLabel>{t.teamLabel}</SectionLabel>
          <h2 className="mt-5 text-3xl md:text-4xl font-medium tracking-tight leading-[1.1]">{t.teamTitle}</h2>
          <p className="mt-6 text-white/70 leading-relaxed max-w-3xl mb-12">{t.teamIntro}</p>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
            {c.team.map((m, i) => (
              <div key={i} className="group">
                <div className="relative overflow-hidden rounded-xl aspect-[3/4]">
                  <img src={m.image} alt={m.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                </div>
                <h3 className="mt-5 text-white text-lg font-medium">{m.name}</h3>
                <div className="text-white/50 text-xs tracking-wider mt-1 mb-3">{m.role}</div>
                <p className="text-white/55 text-sm">{m.bio}</p>
              </div>
            ))}
          </div>
        </section>

        {/* AFSLUITING — persoonlijke boodschap + CTA */}
        <section className="mt-28 lg:mt-36 rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent px-7 py-14 md:px-16 md:py-20 text-center" data-testid="over-closing">
          <h2 className="text-3xl md:text-5xl font-medium tracking-tight leading-[1.08] max-w-3xl mx-auto">{t.closingTitle}</h2>
          <p className="mt-7 text-white/75 text-lg leading-relaxed max-w-2xl mx-auto">{t.closing}</p>
          <p className="mt-5 text-white/60 leading-relaxed max-w-2xl mx-auto">{t.closingInvite}</p>
          <div className="mt-10 flex justify-center">
            <BookingCTA size="lg" align="center" waMessage={t.teamWa} offerteTreatment={ui.over.label} />
          </div>
        </section>

        {/* MERKEN */}
        <section className="mt-28 lg:mt-36" data-testid="over-brands">
          <SectionLabel>{t.brandsLabel}</SectionLabel>
          <h2 className="mt-5 text-3xl md:text-4xl font-medium tracking-tight leading-[1.1] max-w-3xl mb-6">{t.brandsTitle}</h2>
          <p className="text-white/60 max-w-2xl mb-12">{t.brandsText}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {c.brands.map(b => (
              <div key={b.name} className="p-6 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.05] transition-colors text-center">
                <div className="text-white text-xl md:text-2xl font-semibold tracking-tight">{b.name}</div>
                <div className="mt-2 text-white/45 text-xs">{b.tagline}</div>
              </div>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
};

export default Over;
